/**
 * UC-A-3 (UI-03) — one actionable alert for the Shift Details sheet. Priority: accidental worker conflict → open coverage →
 * segment validation error → attendance gap (delivered). Never billing text. Pure (Jest-tested).
 */
import { isDelivered, shiftSpan, toMinutes, plusOne } from './shiftSheetRules';

const segMinutes = (shift, row) => {
  const { start: S } = shiftSpan(shift);
  let s = toMinutes(row.allocation_start_time) ?? 0;
  let e = toMinutes(row.allocation_end_time) ?? s;
  if (s < S) s += 1440;
  if (e <= s) e += 1440;
  return [s, e];
};

/** Client-visible plan issues (§17): segments outside the shift window, overlapping each other, or none rostered. */
export const segmentIssues = (shift, segments = []) => {
  if (!shift?.participant_id) return [];
  const rows = (segments || []).filter((r) => r.status !== 'DELETED');
  if (!rows.length) return [{ code: 'NO_SEGMENT', text: 'No service segment rostered for this participant yet.' }];
  const { start: S, end: E } = shiftSpan(shift);
  const spans = rows.map((r) => ({ r, span: segMinutes(shift, r) })).sort((a, b) => a.span[0] - b.span[0]);
  const out = [];
  spans.forEach(({ r, span: [s, e] }) => {
    if (s < S || e > E) out.push({ code: 'SEGMENT_OUTSIDE_SHIFT', text: `Segment ${plusOne(s)}–${plusOne(e)} sits outside the participant window.`, segment_id: r.id });
  });
  for (let i = 1; i < spans.length; i += 1) {
    if (spans[i].span[0] < spans[i - 1].span[1]) out.push({ code: 'SEGMENT_OVERLAP', text: `Segments ${plusOne(spans[i - 1].span[0])}–${plusOne(spans[i - 1].span[1])} and ${plusOne(spans[i].span[0])}–${plusOne(spans[i].span[1])} overlap.`, segment_id: spans[i].r.id });
  }
  return out;
};

/** Delivered shift with a segment that has no delivery outcome, or a participant attendance row still unrecorded. */
export const attendanceGaps = (shift, segments = [], group = null) => {
  if (!isDelivered(shift)) return [];
  const out = (segments || []).filter((r) => r.status !== 'DELETED' && !r.delivery_outcome)
    .map((r) => ({ code: 'OUTCOME_MISSING', text: `Delivery outcome not recorded for ${r.allocation_start_time}–${r.allocation_end_time}.`, segment_id: r.id }));
  const unrec = (group?.unrecorded_participants || []).map(String);
  if (shift?.participant_id && unrec.includes(String(shift.participant_id))) out.push({ code: 'ATTENDANCE_UNRECORDED', text: 'Participant attendance has not been recorded.' });
  return out;
};

/** UA-6-F19 (§54 reuse): workers an ungrouped shift needs — server-derived `workers_required` (S2 segment ratios / required_staff),
 *  else the max worker count of the segment ratios passed in, else required_staff, else 1. Pure. */
export const ratioWorkers = (ratio) => { const w = parseInt(String(ratio || '').split(':')[0], 10); return Number.isFinite(w) && w > 0 ? w : 1; };
export const workersRequired = (shift, segments = []) => Math.max(
  Number(shift?.workers_required) || 0, Number(shift?.required_staff) || 1,
  ...(segments || []).filter((r) => r.status !== 'DELETED').map((r) => ratioWorkers(r.operational_ratio)), 1);
export const workersAssigned = (shift) => (shift?.assigned_staff || []).length;
/** "2:1 planned · 1 of 2 workers" when understaffed, else null. */
export const understaffedText = (shift, segments = []) => {
  const need = workersRequired(shift, segments); const have = workersAssigned(shift);
  return need > 1 && have < need ? `${need}:1 planned · ${have} of ${need} workers` : null;
};

/** Open coverage periods: group OPEN slices, or the whole window when an ungrouped shift has fewer workers than planned. */
export const openCoverage = (shift, group = null, segments = []) => {
  if (group) return (group.ratio_slices || []).filter((s) => s.open_coverage || s.ratio === 'OPEN').map((s) => ({ start_time: s.start_time, end_time: s.end_time }));
  const terminal = ['CANCELLED', 'COMPLETED', 'NO_SHOW'].includes(String(shift?.status || '').toUpperCase());
  const need = workersRequired(shift, segments); const have = workersAssigned(shift);
  if (!terminal && have < need) return [need > 1 ? { start_time: shift?.start_time, end_time: shift?.end_time, workers_missing: need - have } : { start_time: shift?.start_time, end_time: shift?.end_time }];
  return [];
};

export const topAlert = ({ shift, conflicts = [], group = null, segments = [], issues, gaps } = {}) => {
  if (conflicts.length) {
    const c = conflicts[0];
    return { kind: 'WORKER_CONFLICT', tone: 'danger', text: `${c.worker_name}: ${c.reason}`, action: { label: 'Review coverage', tab: 'coverage' } };
  }
  const open = openCoverage(shift, group, segments);
  if (open.length) {
    const under = !group && understaffedText(shift, segments);
    return { kind: 'OPEN_COVERAGE', tone: 'warning',
      text: under ? `${under} — open coverage ${open.map((o) => `${o.start_time}–${o.end_time}`).join(', ')}.` : `Open coverage ${open.map((o) => `${o.start_time}–${o.end_time}`).join(', ')} — no worker assigned.`,
      action: { label: under ? 'Add second worker' : 'Assign worker', tab: 'coverage' } };
  }
  const segIssues = issues || segmentIssues(shift, segments);
  if (segIssues.length) return { kind: 'SEGMENT_ISSUE', tone: 'warning', text: segIssues[0].text, action: { label: 'Fix plan', tab: 'plan' } };
  const g = gaps || attendanceGaps(shift, segments, group);
  if (g.length) return { kind: 'ATTENDANCE_GAP', tone: 'info', text: g[0].text, action: { label: 'Record attendance', tab: 'attendance' } };
  return null;
};
